import { z } from 'zod';
import { rsvpParticipantSchema, participantDraftSchema } from './IEventRegistration';

export const rsvpStatusSchema = z.enum(['yes', 'no', 'maybe']);
export type RsvpStatus = z.infer<typeof rsvpStatusSchema>;

export const rsvpApiStatusSchema = z.enum(['GOING', 'NOT_GOING', 'MAYBE']);
export type RsvpApiStatus = z.infer<typeof rsvpApiStatusSchema>;

export const rsvpUserSchema = z.object({
    id: z.number(),
    first_name: z.string().nullable().optional().default(''),
    last_name: z.string().nullable().optional().default(''),
    username: z.string().default(''),
    profile_picture: z.string().nullable().optional().default(null),
});

export const eventRsvpSchema = z.object({
    id: z.number().optional(),
    event_id: z.number(),
    user: rsvpUserSchema,
    status: rsvpStatusSchema,
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    guest_count: z.number().int().nonnegative().default(0),
    participants: z.array(rsvpParticipantSchema).default([]),
});

export type IEventRsvp = z.infer<typeof eventRsvpSchema>;

export const eventRsvpListItemSchema = z.object({
    user: z.object({
        id: z.number(),
        first_name: z.string().nullable().optional().default(''),
        last_name: z.string().nullable().optional().default(''),
        email: z.string().email().optional(),
    }),
    status: rsvpApiStatusSchema,
    updatedAt: z.string().optional(),
    guest_count: z.number().int().nonnegative().default(0),
    participants: z.array(rsvpParticipantSchema).default([]),
});

export type IEventRsvpListItem = z.infer<typeof eventRsvpListItemSchema>;

export const eventRsvpListResponseSchema = z.object({
    eventId: z.number(),
    going_count: z.number().default(0),
    not_going_count: z.number().default(0),
    maybe_count: z.number().default(0),
    guest_count: z.number().default(0),
    attendee_count: z.number().default(0),
    rsvps: z.array(eventRsvpListItemSchema).default([]),
});

export type IEventRsvpListResponse = z.infer<typeof eventRsvpListResponseSchema>;

export const rsvpUpsertResponseSchema = z.object({
    status: rsvpApiStatusSchema.nullable(),
});

export type RsvpUpsertResponse = z.infer<typeof rsvpUpsertResponseSchema>;

export const reactionEmojiSchema = z.enum(['❤️', '👍', '🍺']);
export type ReactionKind = z.infer<typeof reactionEmojiSchema>;

export const reactionKindApiSchema = z.enum(['heart', 'like', 'beer']);

export const reactionDtoSchema = z.object({
    user_id: z.number().int(),
    user_name: z.string().optional(),
    emoji: reactionEmojiSchema.optional(),
    kind: reactionKindApiSchema.optional(),
});

export type IReactionDto = z.infer<typeof reactionDtoSchema>;

export const normalizedReactionSchema = z.object({
    user_id: z.number().int(),
    user_name: z.string().default(''),
    emoji: reactionEmojiSchema,
});

export type IReaction = z.infer<typeof normalizedReactionSchema>;

export const reactionCountsSchema = z.object({
    '❤️': z.number().int().nonnegative().default(0),
    '👍': z.number().int().nonnegative().default(0),
    '🍺': z.number().int().nonnegative().default(0),
});

export type ReactionCounts = z.infer<typeof reactionCountsSchema>;
export type ReactionUserNames = Record<ReactionKind, string[]>;

export const commentDtoSchema = z.object({
    id: z.number(),
    calendar_entry_id: z.number(),
    text: z.string().trim().min(1),
    parent_id: z.number().nullable(),
    created_at: z.string(),
    updated_at: z.string().nullable().optional(),
    user: z.object({
        id: z.number(),
        name: z.string(),
        avatar_url: z.string().nullable().optional().default(null),
    }),
    reactions: z.array(reactionDtoSchema).default([]),
});

export type IEventCommentDto = z.infer<typeof commentDtoSchema>;

export interface IEventComment extends Omit<IEventCommentDto, 'reactions'> {
    reactions: IReaction[];
    viewerEmoji: ReactionKind | null;
    counts: ReactionCounts;
    reactionUserNames: ReactionUserNames;
    replies: IEventComment[];
}

export const commentCreateInputSchema = z.object({
    event_id: z.number(),
    text: z.string().trim().min(1).max(3000),
    parent_id: z.number().int().nullable().optional(),
});

export type CommentCreateInput = z.infer<typeof commentCreateInputSchema>;

export const commentUpdateInputSchema = z.object({
    comment_id: z.number(),
    text: z.string().trim().min(1).max(3000),
});

export type CommentUpdateInput = z.infer<typeof commentUpdateInputSchema>;

export const deleteCommentInputSchema = z.object({
    comment_id: z.number(),
    event_id: z.number(),
});

export type DeleteCommentInput = z.infer<typeof deleteCommentInputSchema>;

export const toggleReactionInputSchema = z.object({
    comment_id: z.number(),
    event_id: z.number(),
    emoji: reactionEmojiSchema,
});

export type ToggleReactionInput = z.infer<typeof toggleReactionInputSchema>;

export const reactionToggleResponseSchema = z.object({
    comment_id: z.number().int(),
    viewer_emoji: reactionEmojiSchema.nullable(),
    counts: reactionCountsSchema,
    reactions: z.array(reactionDtoSchema).default([]),
});
export type ReactionToggleResponse = z.infer<typeof reactionToggleResponseSchema>;

export const setRsvpInputSchema = z.object({
    eventId: z.number(),
    status: rsvpStatusSchema.nullable(),
    participants: z.array(participantDraftSchema).optional(),
});

export type SetRsvpInput = z.infer<typeof setRsvpInputSchema>;
